/* Live pages (UX-187). A page that keeps itself current (the staff app's project page) raises
   LiveRefresh's number each time someone else changed what it shows. React Query data refetches
   by itself; a component that loads its own data (comments) reloads with useLiveReload.

   Anything inside an element marked data-live-safe is left out of the "don't refresh while
   they're editing" check: a comment box never resets when the data around it reloads. */

import { createContext, useContext, useEffect, useRef } from "react";

/** Goes up by one each time the page's data changed elsewhere; 0 where nothing provides it. */
export const LiveRefresh = createContext(0);

/** The attribute that marks an area whose typing never holds back a refresh. */
export const LIVE_SAFE = "data-live-safe";

/** Runs `reload` each time LiveRefresh goes up (not on the first render). */
export function useLiveReload(reload: () => unknown) {
  const tick = useContext(LiveRefresh);
  const seen = useRef(tick);
  const fn = useRef(reload);
  fn.current = reload;
  useEffect(() => {
    if (seen.current === tick) return;
    seen.current = tick;
    void fn.current();
  }, [tick]);
}
